import {Action, getModule, Module} from 'vuex-module-decorators';
import store from '@/store';
import {httpClient} from '@/api/baseApi';
import {EntityModule} from './entityModule';
import {HardwareData, HardwareModuleSchema, HardwarePModule} from '@/system/hardware/interfaces';
import {HardwareDataType} from '@/system/hardware/enums';
/*
    Динамический модуль хранилища vuex для работы с аппаратными модулями
*/

const moduleName = 'hardwareModule';

function transformGet(elem: any): HardwareData {
  return {
    id: elem.id,
    name: elem.name,
    description: elem.description,
    mac: elem.mac,
    type_id: elem.type_id as HardwareDataType,
    pmodules: elem.pmodules,
  };
}

function getPmodules(elem: any): HardwarePModule[] {
  return elem.pmodules?.map((elem: any) => ({
    id: elem.id,
    guid: elem.guid,
    port: elem.port,
  }));
}

function transformSchema(elem: any): HardwareModuleSchema[] {
  return elem.map((elem: any) => ({
    id: elem.id,
    name: elem.name,
    settings: elem.settings,
    image: elem.image,
  }));
}

function transformSchemaGet(elem: any): HardwareModuleSchema {
  return {
    id: elem.id,
    name: elem.name,
    settings: elem.settings,
    image: elem.image,
  };
}

function transformCreateEdit(elem: any): HardwareData {
  const pmodules = getPmodules(elem);
  return {
    id: elem.id,
    name: elem.name,
    description: elem.description,
    mac: elem.mac,
    type_id: elem.type_id as HardwareDataType,
    pmodules,
  };
}

function transformSave(model: HardwareData): any {
  return {
    name: model.name,
    description: model.description,
    mac: model.mac,
    type_id: model.type_id ?? null,
    pmodules: model.pmodules,
  };
}

function transformPmodule(model: {hw_id: number; pm_guid: string; port: number}) {
  return {
    hw_id: model.hw_id,
    pm_guid: model.pm_guid,
    port: model.port,
  };
}

@Module({
  name: moduleName,
  store: store,
  dynamic: true,
  namespaced: true,
})
export class HardwareModule extends EntityModule {
  protected routeName = 'hardware';

  get all(): HardwareData[] {
    return super.all as HardwareData[];
  }

  //Получение списка объектов с сервера
  @Action
  public async fetchAll() {
    return await super.fetchAll(transformGet);
  }

  //Получение модели для создания объекта
  @Action
  public async create(): Promise<{data: HardwareData}> {
    return super.create(transformCreateEdit) as Promise<{data: HardwareData}>;
  }

  //Получение модели для редактирования объекта
  @Action
  public async edit(id: any): Promise<{data: HardwareData}> {
    try {
      const {data} = await httpClient.get(`${this.routeName}/${id}`);
      const transformData = transformCreateEdit(data);
      transformData.id = id;
      return {data: transformData};
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //Создание/реактирование объекта
  @Action
  public async save(model: HardwareData) {
    return super.save(model, transformSave);
  }

  //Связывание прогрммного модуля с портом конкретного аппаратного модуля
  @Action
  public async linkHM(params: {hw_id: number; pm_guid: string; port: number}): Promise<boolean> {
    await httpClient.post(
      `${this.routeName}/pmmodules/`,
      transformPmodule({hw_id: params.hw_id, pm_guid: params.pm_guid, port: params.port}),
    );
    return true;
  }

  //Удаление программного модуля из аппаратного
  @Action
  public async deletePmmodule(id: any) {
    try {
      await httpClient.delete(`${this.routeName}/pmmodules/?uid=${id}`);
    } catch (e) {
      return Promise.reject(e);
    }
  }

  //Загрузка мнемосхемы для узла
  @Action({rawError: true})
  public async uploadScheme(params: {id: number; formData: FormData}) {
    try {
      const {data} = await httpClient.post<{content: string}>(
        `/${this.routeName}/types/${params.id}/schema/`,
        params.formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      return data.content;
    } catch (e) {
      return Promise.reject(e);
    }
  }

  // Получение мнемосхемы для аппаратного модуля
  @Action
  public async getScheme(id: number) {
    try {
      const {data} = await httpClient.get<string>(`/${this.routeName}/types/${id}/schema/`);
      return data;
    } catch {
      return null;
    }
  }

  // Получить настройки для АП по порту и типу АМ
  @Action
  public async getPMSettings(params: {hw_type_id: number; port: number; pm_type: string}) {
    try {
      const {data} = await httpClient.get<string>(
        `/${this.routeName}/types/settings/${params.hw_type_id}/${params.port}/${params.pm_type}`,
      );
      return data;
    } catch {
      return null;
    }
  }

  //Получение схем АМ
  @Action
  public async getModuleScheme() {
    try {
      const {data} = await httpClient.get<string>(`${this.routeName}/types`);
      return transformSchema(data);
    } catch {
      return null;
    }
  }

  //Получение схем АМ по Id
  @Action
  public async getModuleSchemeById(id: any) {
    try {
      const {data} = await httpClient.get<string>(`${this.routeName}/types/${id}`);
      return transformSchemaGet(data);
    } catch {
      return null;
    }
  }

  @Action
  public async getById(id: number) {
    return super.getById(id, transformCreateEdit) as Promise<{data: HardwareData}>;
  }
}

export default getModule(HardwareModule);
